import React, { useState, useEffect } from "react";
import { X, Lock, Key, ShieldCheck, Sparkles, AlertCircle, RefreshCw } from "lucide-react";
import LLMModelsConfig from "./LLMModelsConfig";

export const LLMProviderModal = ({
  open,
  provider = null, // null para crear, objeto para editar
  templates = [],
  onClose,
  onSave,
  loading = false,
}) => {
  const isEdit = Boolean(provider?.id);

  const [providerType, setProviderType] = useState("deepseek");
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [models, setModels] = useState([]);
  const [isReplacingKey, setIsReplacingKey] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Inicializar formulario
  useEffect(() => {
    if (!open) return;

    if (isEdit && provider) {
      setProviderType(provider.provider_type);
      setLabel(provider.label);
      setBaseUrl(provider.base_url);
      setApiKey("");
      setModels(provider.models || []);
      setIsReplacingKey(false);
      setErrorMsg(null);
    } else {
      // Nuevo: cargar primera plantilla por defecto
      const defaultTmpl = templates.find((t) => t.provider_type === "deepseek") || templates[0];
      if (defaultTmpl) {
        setProviderType(defaultTmpl.provider_type);
        setLabel(defaultTmpl.default_label);
        setBaseUrl(defaultTmpl.default_base_url);
        setModels(defaultTmpl.suggested_models || []);
      } else {
        setProviderType("deepseek");
        setLabel("DeepSeek");
        setBaseUrl("https://api.deepseek.com");
        setModels([]);
      }
      setApiKey("");
      setIsReplacingKey(false);
      setErrorMsg(null);
    }
  }, [open, isEdit, provider, templates]);

  if (!open) return null;

  const handleTemplateSelect = (tmplType) => {
    setProviderType(tmplType);
    const tmpl = templates.find((t) => t.provider_type === tmplType);
    if (tmpl) {
      setLabel(tmpl.default_label);
      setBaseUrl(tmpl.default_base_url);
      setModels(tmpl.suggested_models || []);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!label.trim()) {
      setErrorMsg("El nombre del proveedor es obligatorio.");
      return;
    }

    if (!baseUrl.trim()) {
      setErrorMsg("La URL base es obligatoria.");
      return;
    }

    if (!baseUrl.startsWith("https://") && !baseUrl.startsWith("http://localhost")) {
      setErrorMsg("La URL debe iniciar con 'https://' por seguridad anti-SSRF.");
      return;
    }

    const payload = {
      provider_type: providerType,
      label: label.trim(),
      base_url: baseUrl.trim(),
      models: models,
    };

    // Manejo seguro de API Key:
    // Solo enviar api_key si es creación nueva o si el usuario activó expresamente el reemplazo
    if (!isEdit) {
      if (apiKey.trim()) {
        payload.api_key = apiKey.trim();
      }
    } else {
      if (isReplacingKey) {
        payload.api_key = apiKey.trim(); // si está vacía, el backend eliminará la clave
      }
      // Si no se reemplazó, api_key se omite del payload y se preserva la clave cifrada existente
    }

    onSave(payload);
  };

  return (
    <div
      data-testid="llm-provider-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
    >
      <div
        data-testid="llm-provider-modal"
        className="my-8 w-full max-w-2xl rounded-2xl border border-[#2a2a2a] bg-[#141414] shadow-2xl p-6 text-left"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-[#262626] pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#f5b544]/30 bg-[#f5b544]/10 text-[#f5b544]">
              <Sparkles size={20} />
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-[#ededed]">
                {isEdit ? `Editar Conexión: ${provider.label}` : "Configurar Proveedor LLM"}
              </h3>
              <p className="text-xs text-[#8a8a8a]">
                Conexión independiente para el Creative Director IA (aislada de APIMart)
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="llm-modal-close-btn"
            disabled={loading}
            onClick={onClose}
            className="rounded-lg p-1.5 text-[#8a8a8a] hover:bg-[#222] hover:text-[#ededed] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Error banner si aplica */}
        {errorMsg && (
          <div
            data-testid="llm-modal-error-banner"
            className="mt-4 flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-300"
          >
            <AlertCircle size={15} className="shrink-0 text-red-400" />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-5">
          {/* Selector de Plantilla (solo en creación) */}
          {!isEdit && templates.length > 0 && (
            <div>
              <span className="block text-xs font-medium text-[#a3a3a3] mb-2">
                Seleccionar Proveedor:
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {templates.map((tmpl) => {
                  const selected = providerType === tmpl.provider_type;
                  return (
                    <button
                      key={tmpl.provider_type}
                      type="button"
                      data-testid={`llm-template-${tmpl.provider_type}`}
                      onClick={() => handleTemplateSelect(tmpl.provider_type)}
                      className={`flex flex-col items-center justify-center rounded-xl border p-2.5 text-center transition-all ${
                        selected
                          ? "border-[#f5b544] bg-[#f5b544]/10 text-[#f5b544]"
                          : "border-[#282828] bg-[#181818] text-[#a3a3a3] hover:border-[#383838] hover:text-[#ededed]"
                      }`}
                    >
                      <span className="text-xs font-semibold">{tmpl.default_label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Campos básicos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#a3a3a3]">Nombre / Etiqueta *</span>
              <input
                data-testid="llm-provider-label-input"
                className="v-input text-xs"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="DeepSeek Producción"
                required
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#a3a3a3]">URL Base de la API *</span>
              <input
                data-testid="llm-provider-url-input"
                className="v-input text-xs font-mono"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.deepseek.com"
                required
              />
              <span className="text-[10px] text-[#737373]">
                Protegido por anti-SSRF y anclaje de IP. Requiere https://.
              </span>
            </label>
          </div>

          {/* Gestión segura de API Key */}
          <div className="rounded-xl border border-[#262626] bg-[#181818] p-4 text-xs">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 font-medium text-[#ededed]">
                <Key size={14} className="text-[#f5b544]" />
                <span>Credenciales (API Key)</span>
              </div>
              {isEdit && provider?.has_key && !isReplacingKey && (
                <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                  <ShieldCheck size={12} /> Cifrada en bóveda
                </span>
              )}
            </div>

            {isEdit && provider?.has_key && !isReplacingKey ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[#282828] bg-[#121212] p-2.5">
                <div className="flex items-center gap-2">
                  <Lock size={13} className="text-[#888]" />
                  <span className="font-mono text-[#a3a3a3] text-[11px]">
                    {provider.key_hint || "••••••••••••••••"}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="llm-replace-key-btn"
                  onClick={() => setIsReplacingKey(true)}
                  className="rounded border border-[#383838] bg-[#222] px-2 py-1 text-[11px] text-[#ededed] hover:border-[#f5b544]/50"
                >
                  Cambiar clave
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <input
                  type="password"
                  data-testid="llm-provider-key-input"
                  className="v-input text-xs font-mono"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={isEdit ? "Escriba la nueva clave (o deje vacía para desconfigurar)" : "sk-..."}
                />
                <div className="flex items-center justify-between text-[11px] text-[#737373]">
                  <span>
                    La clave se cifrará inmediatamente en el servidor usando AES-128-CBC + HMAC-SHA256.
                  </span>
                  {isEdit && isReplacingKey && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsReplacingKey(false);
                        setApiKey("");
                      }}
                      className="text-[#f5b544] hover:underline"
                    >
                      Conservar clave actual
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Subcomponente de Modelos Dinámicos */}
          <div className="border-t border-[#262626] pt-4">
            <LLMModelsConfig
              models={models}
              onChange={(newModels) => setModels(newModels)}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-[#262626] pt-4">
            <button
              type="button"
              data-testid="llm-modal-cancel-btn"
              disabled={loading}
              onClick={onClose}
              className="v-btn-ghost v-btn-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="llm-modal-submit-btn"
              disabled={loading}
              className="v-btn-primary v-btn-sm inline-flex items-center gap-2"
            >
              {loading ? (
                <span>Guardando…</span>
              ) : (
                <>
                  <ShieldCheck size={14} />
                  <span>{isEdit ? "Guardar Cambios" : "Registrar Proveedor"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LLMProviderModal;
